import React, { useEffect, useRef, useState } from 'react';
import { getUsuarioActual } from '../auth/usuario';
import { alRenovarToken, getSsoAppsUrl, logout } from '../config/keycloak.config';

/**
 * Menú de usuario del encabezado institucional.
 *
 * Tres cosas, en el orden del estándar visual del workspace (ver el `profile`
 * de `TablerosPowerBi/src/components/Layout.tsx`): quién es el usuario, el
 * salto al portal de apps y el cierre de sesión.
 *
 * Ambas salidas llevan al portal de aplicaciones. "Todas las Apps" navega sin
 * tocar la sesión; "Cerrar sesión" cierra también la del realm antes de
 * mandarlo allá, para que el chequeo silencioso de otra app no lo vuelva a
 * autenticar solo (KEYCLOAK_LOGIN_STANDARD.md §5).
 */
export const UserMenu: React.FC = () => {
  const [abierto, setAbierto] = useState(false);
  // El token se renueva cada pocos minutos y puede traer claims nuevos (un
  // cambio de nombre o de roles en Keycloak); esto fuerza el re-render.
  const [, setVersionToken] = useState(0);
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => alRenovarToken(() => setVersionToken(v => v + 1)), []);

  useEffect(() => {
    if (!abierto) return;

    const alClicFuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };

    document.addEventListener('mousedown', alClicFuera);
    document.addEventListener('keydown', alEscape);
    return () => {
      document.removeEventListener('mousedown', alClicFuera);
      document.removeEventListener('keydown', alEscape);
    };
  }, [abierto]);

  const usuario = getUsuarioActual();

  return (
    <div ref={contenedor} className="relative">
      <button
        type="button"
        onClick={() => setAbierto(v => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className="flex items-center gap-2.5 max-w-full rounded-xl border border-gray-200 bg-white pl-1.5 pr-2.5 py-1.5 hover:bg-gray-50 hover:border-gray-300 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[#0943B5]/30"
      >
        <span className="w-8 h-8 shrink-0 rounded-lg bg-[#0943B5] text-white text-xs font-semibold flex items-center justify-center">
          {usuario.iniciales}
        </span>
        <span className="hidden sm:block min-w-0 text-left">
          <span className="block text-sm font-medium text-[#0a0c46] truncate max-w-[10rem]">
            {usuario.nombreCompleto}
          </span>
          {usuario.email && (
            <span className="block text-[11px] text-gray-500 truncate max-w-[10rem]">
              {usuario.email}
            </span>
          )}
        </span>
        <svg
          className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-150 ${abierto ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {abierto && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-[#0a0c46] truncate" title={usuario.nombreCompleto}>
              {usuario.nombreCompleto}
            </p>
            <p className="text-xs text-gray-500 truncate" title={usuario.email ?? usuario.username}>
              {usuario.email ?? usuario.username}
            </p>
          </div>

          <a
            href={getSsoAppsUrl()}
            role="menuitem"
            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Todas las Apps
          </a>

          <button
            type="button"
            role="menuitem"
            onClick={logout}
            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100 transition-colors duration-150"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
};
